import { useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX, X } from "lucide-react";
import { Eyebrow, Heading } from "./Section";
import { GALLERY, GALLERY_VIDEO, VIDEO_POSTER } from "../data";

export const Gallery = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
  }, []);

  useEffect(() => {
    if (!videoRef.current || reducedMotion) return;
    videoRef.current
      .play()
      .then(() => setPlaying(true))
      .catch(() => setPlaying(false));
  }, [reducedMotion]);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <section id="gallery" data-testid="gallery" className="py-20 md:py-28 bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-10">
          <Eyebrow>The Work</Eyebrow>
          <Heading>SEE THE CUTS</Heading>
        </div>

        {/* Featured video — the gallery's hero moment. Full width on mobile,
            capped + centered on larger screens since the source is vertical (9:16). */}
        <div className="group relative w-full sm:max-w-[420px] sm:mx-auto mb-8 aspect-[9/16] rounded-xl overflow-hidden border border-white/10 bg-charcoal">
          <video
            ref={videoRef}
            data-testid="gallery-featured-video"
            className="w-full h-full object-cover cursor-pointer"
            src={GALLERY_VIDEO}
            poster={VIDEO_POSTER}
            muted={muted}
            loop
            playsInline
            preload="metadata"
            onClick={togglePlay}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />

          <span className="absolute top-4 left-4 font-mono-u text-[11px] tracking-widest uppercase text-cream bg-barber-red px-3 py-1">
            Featured
          </span>

          {!playing && (
            <button
              onClick={togglePlay}
              aria-label="Play video"
              data-testid="gallery-video-play"
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="w-16 h-16 rounded-full bg-black/70 flex items-center justify-center group-hover:bg-barber-red transition-colors">
                <Play size={26} className="text-cream ml-0.5" />
              </span>
            </button>
          )}

          <button
            onClick={toggleMute}
            data-testid="gallery-video-mute-toggle"
            aria-label={muted ? "Unmute video" : "Mute video"}
            className="absolute bottom-4 right-4 bg-black/70 text-cream p-2.5 rounded-full hover:bg-barber-red transition-colors"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        {/* Photo grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {GALLERY.map((src, i) => (
            <button
              key={i}
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightboxIndex(i)}
              className="relative aspect-square rounded-lg overflow-hidden border border-white/10 group"
            >
              <img
                src={src}
                alt={`AK Cuts haircut ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 ring-2 ring-inset ring-barber-red/0 group-hover:ring-barber-red/80 transition-all duration-300" />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            aria-label="Close"
            data-testid="gallery-lightbox-close"
            className="absolute top-5 right-5 text-cream p-2"
            onClick={() => setLightboxIndex(null)}
          >
            <X size={28} />
          </button>
          <img
            src={GALLERY[lightboxIndex]}
            alt={`AK Cuts haircut ${lightboxIndex + 1}`}
            className="max-h-[85vh] max-w-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};
